/**
 * NotificationPreferencesPage — Story 95-9.
 *
 * `/settings/notifications` — user-facing settings panel that lists every
 * Story 95-7 notification type grouped by domain (accordion), exposes three
 * channel toggles per row (email / sms / in-app), and lets the user pin a
 * daily quiet-hours window with a timezone override.
 *
 * The backend `NotificationPreference` repository already exists (Story
 * 95-1 schema + 95-2 scaffold). The REST controller exposing it to the web
 * client lands alongside this story; until it is wired, `listMyNotificationPreferences()`
 * degrades to an empty array and the UI paints a clean default state.
 *
 * FRANCHISE_ADMIN users additionally see an "Admin reset" block at the
 * bottom where they can wipe another user's preferences and revert to
 * catalog defaults — a self-service version of the standard "I'm not
 * getting emails" support request.
 *
 * i18n: every label is keyed under `settings.notificationPrefs.*` and
 * `notifications.types.*.{label,description}` across 4 locales (hu/en/de/uk).
 */

import {
  listMyNotificationPreferences,
  resetUserNotificationPreferences,
  upsertNotificationPreference,
  type NotificationChannel,
  type NotificationPreferenceRow,
} from '@/api/notification-preferences';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslation } from '@kgc/i18n';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@kgc/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Catalog (frontend-local, kept in sync with @kgc/messaging-gateway)
// ---------------------------------------------------------------------------

/**
 * Frontend-local mirror of `NOTIFICATION_TYPE_REGISTRY` from
 * `packages/integration/messaging-gateway/src/notification/types/notification-types.ts`.
 * Duplicated here so the page does not pull the full `@prisma/client`
 * dependency graph into the web bundle — the shared package has a
 * runtime import of `NotificationPriority` from Prisma.
 *
 * Review gate: when a new notification type lands in `NotificationTypes`,
 * add it here too. The `notifications.types.<key>.{label,description}`
 * i18n path must be populated in all 4 locales at the same time.
 */
interface NotificationTypeEntry {
  key: string;
  domain: NotificationDomain;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
  defaultChannels: readonly NotificationChannel[];
}

type NotificationDomain =
  | 'vehicle'
  | 'rental'
  | 'service'
  | 'sales'
  | 'finance'
  | 'workflow'
  | 'documents'
  | 'partner';

const NOTIFICATION_CATALOG: readonly NotificationTypeEntry[] = [
  {
    key: 'vehicle.document.expiring',
    domain: 'vehicle',
    priority: 'HIGH',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'vehicle.document.expired',
    domain: 'vehicle',
    priority: 'CRITICAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'vehicle.service.due',
    domain: 'vehicle',
    priority: 'NORMAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'rental.overdue',
    domain: 'rental',
    priority: 'CRITICAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'rental.return.reminder',
    domain: 'rental',
    priority: 'NORMAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'rental.contract.signed',
    domain: 'rental',
    priority: 'NORMAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'service.worksheet.ready',
    domain: 'service',
    priority: 'NORMAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'service.warranty.expiring',
    domain: 'service',
    priority: 'LOW',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'sales.invoice.issued',
    domain: 'sales',
    priority: 'NORMAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'sales.deliveryNote.signed',
    domain: 'sales',
    priority: 'NORMAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'finance.payment.overdue',
    domain: 'finance',
    priority: 'HIGH',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'finance.cash-close.due',
    domain: 'finance',
    priority: 'NORMAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'workflow.approval.requested',
    domain: 'workflow',
    priority: 'HIGH',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'workflow.execution.failed',
    domain: 'workflow',
    priority: 'CRITICAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'documents.hash.mismatch',
    domain: 'documents',
    priority: 'CRITICAL',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'documents.retention.expired',
    domain: 'documents',
    priority: 'LOW',
    defaultChannels: ['email', 'in-app'],
  },
  {
    key: 'partner.blocklisted',
    domain: 'partner',
    priority: 'CRITICAL',
    defaultChannels: ['email', 'in-app'],
  },
] as const;

const DOMAIN_ORDER: readonly NotificationDomain[] = [
  'vehicle',
  'rental',
  'service',
  'sales',
  'finance',
  'workflow',
  'documents',
  'partner',
];

const CHANNELS: readonly NotificationChannel[] = ['email', 'sms', 'in-app'];

/**
 * Whitelisted IANA zones shown in the quiet-hours timezone picker. Covers
 * the 4 UI languages' home regions + UTC for power users. Extend as
 * needed via a follow-up story — a full IANA dropdown (~580 zones) would
 * be a UX disaster on mobile.
 */
const TIMEZONES: readonly { value: string; label: string }[] = [
  { value: 'Europe/Budapest', label: 'Europe/Budapest (hu)' },
  { value: 'Europe/Vienna', label: 'Europe/Vienna (de)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (de)' },
  { value: 'Europe/Kiev', label: 'Europe/Kiev (uk)' },
  { value: 'Europe/London', label: 'Europe/London (en)' },
  { value: 'UTC', label: 'UTC' },
];

// ---------------------------------------------------------------------------
// Helpers (exported for testing)
// ---------------------------------------------------------------------------

export const NOTIFICATION_CATALOG_FOR_TESTS = NOTIFICATION_CATALOG;

export function buildDefaultRow(entry: NotificationTypeEntry): NotificationPreferenceRow {
  return {
    notificationType: entry.key,
    enabledChannels: [...entry.defaultChannels],
    quietHoursStart: null,
    quietHoursEnd: null,
    timezone: null,
  };
}

export function isChannelEnabled(
  row: NotificationPreferenceRow,
  channel: NotificationChannel
): boolean {
  return row.enabledChannels.includes(channel);
}

export function toggleChannel(
  row: NotificationPreferenceRow,
  channel: NotificationChannel
): NotificationPreferenceRow {
  const next = isChannelEnabled(row, channel)
    ? row.enabledChannels.filter(c => c !== channel)
    : [...row.enabledChannels, channel];
  return { ...row, enabledChannels: next };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationPreferencesPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore(s => s.user);
  const isFranchiseAdmin = user?.role === 'FRANCHISE_ADMIN' || user?.role === 'SUPER_ADMIN';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['notification-preferences', 'me'],
    queryFn: listMyNotificationPreferences,
    staleTime: 60_000,
  });

  // Merge server data with catalog defaults — rows missing from the
  // server response are surfaced with their catalog defaults so the user
  // sees every type immediately.
  const rowsByKey = useMemo((): Record<string, NotificationPreferenceRow> => {
    const map: Record<string, NotificationPreferenceRow> = {};
    for (const entry of NOTIFICATION_CATALOG) {
      map[entry.key] = buildDefaultRow(entry);
    }
    for (const row of data ?? []) {
      map[row.notificationType] = { ...map[row.notificationType], ...row };
    }
    return map;
  }, [data]);

  const [localRows, setLocalRows] = useState<Record<string, NotificationPreferenceRow> | null>(
    null
  );
  const effectiveRows = localRows ?? rowsByKey;
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(() => new Set());

  const mutate = useCallback(
    (key: string, transform: (row: NotificationPreferenceRow) => NotificationPreferenceRow) => {
      setLocalRows(prev => {
        const base = prev ?? rowsByKey;
        const next = {
          ...base,
          [key]: transform(base[key] ?? buildDefaultRow(NOTIFICATION_CATALOG[0]!)),
        };
        return next;
      });
      setDirtyKeys(prev => new Set(prev).add(key));
    },
    [rowsByKey]
  );

  const saveMutation = useMutation({
    mutationFn: async (keys: string[]): Promise<void> => {
      for (const key of keys) {
        const row = effectiveRows[key];
        if (!row) continue;
        await upsertNotificationPreference({
          notificationType: row.notificationType,
          enabledChannels: row.enabledChannels,
          quietHoursStart: row.quietHoursStart,
          quietHoursEnd: row.quietHoursEnd,
          timezone: row.timezone,
        });
      }
    },
    onSuccess: async () => {
      setDirtyKeys(new Set());
      setLocalRows(null);
      await queryClient.invalidateQueries({ queryKey: ['notification-preferences', 'me'] });
      await refetch();
    },
  });

  const resetMutation = useMutation({
    mutationFn: (userId: string) => resetUserNotificationPreferences(userId),
  });

  const [adminResetUserId, setAdminResetUserId] = useState<string>('');

  const handleSaveAll = useCallback(() => {
    const keys = Array.from(dirtyKeys);
    if (keys.length === 0) return;
    saveMutation.mutate(keys);
  }, [dirtyKeys, saveMutation]);

  const handleResetAdmin = useCallback(() => {
    const uid = adminResetUserId.trim();
    if (uid.length === 0) return;
    resetMutation.mutate(uid);
  }, [adminResetUserId, resetMutation]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="container mx-auto max-w-4xl p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('settings.notificationPrefs.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('settings.notificationPrefs.subtitle')}
        </p>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            {t('settings.notificationPrefs.loading')}
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-red-500 dark:text-red-400">
            {t('settings.notificationPrefs.loadError')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.notificationPrefs.channelsTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion
                type="multiple"
                className="w-full"
                // All groups default-open so the user can scan the full
                // matrix without first expanding every accordion row.
                defaultValue={[...DOMAIN_ORDER]}
              >
                {DOMAIN_ORDER.map(domain => {
                  const types = NOTIFICATION_CATALOG.filter(e => e.domain === domain);
                  if (types.length === 0) return null;
                  return (
                    <AccordionItem key={domain} value={domain}>
                      <AccordionTrigger>
                        <div className="flex items-center gap-2">
                          <span>{t(`settings.notificationPrefs.domains.${domain}`)}</span>
                          <span className="text-xs text-muted-foreground">({types.length})</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3" data-testid={`notification-domain-${domain}`}>
                          {types.map(entry => {
                            const row = effectiveRows[entry.key] ?? buildDefaultRow(entry);
                            const labelKey = `notifications.types.${entry.key}.label`;
                            const descKey = `notifications.types.${entry.key}.description`;
                            return (
                              <div
                                key={entry.key}
                                className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                                data-testid={`notification-row-${entry.key}`}
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <div className="text-sm font-medium">{t(labelKey)}</div>
                                    {entry.priority === 'CRITICAL' ? (
                                      <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-600 dark:text-red-400">
                                        {t('settings.notificationPrefs.priorityCritical')}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-muted-foreground">{t(descKey)}</div>
                                </div>
                                <div className="flex flex-wrap gap-4 sm:flex-nowrap">
                                  {CHANNELS.map(channel => (
                                    <label
                                      key={channel}
                                      className="flex items-center gap-2 text-xs"
                                    >
                                      <Switch
                                        checked={isChannelEnabled(row, channel)}
                                        onCheckedChange={() =>
                                          mutate(entry.key, r => toggleChannel(r, channel))
                                        }
                                        aria-label={`${t(labelKey)} — ${t(
                                          `settings.notificationPrefs.channels.${channel}`
                                        )}`}
                                        data-testid={`notification-toggle-${entry.key}-${channel}`}
                                      />
                                      <span>
                                        {t(`settings.notificationPrefs.channels.${channel}`)}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('settings.notificationPrefs.quietHoursTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('settings.notificationPrefs.quietHoursHint')}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="quiet-start">
                    {t('settings.notificationPrefs.quietHoursStart')}
                  </Label>
                  <Input
                    id="quiet-start"
                    type="time"
                    value={effectiveRows['vehicle.document.expiring']?.quietHoursStart ?? ''}
                    onChange={e => {
                      const next = e.target.value || null;
                      // Apply the quiet hours to every row — the user
                      // picks ONE global window in the MVP. A per-type
                      // override is a follow-up.
                      for (const entry of NOTIFICATION_CATALOG) {
                        mutate(entry.key, r => ({ ...r, quietHoursStart: next }));
                      }
                    }}
                    data-testid="quiet-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="quiet-end">{t('settings.notificationPrefs.quietHoursEnd')}</Label>
                  <Input
                    id="quiet-end"
                    type="time"
                    value={effectiveRows['vehicle.document.expiring']?.quietHoursEnd ?? ''}
                    onChange={e => {
                      const next = e.target.value || null;
                      for (const entry of NOTIFICATION_CATALOG) {
                        mutate(entry.key, r => ({ ...r, quietHoursEnd: next }));
                      }
                    }}
                    data-testid="quiet-end-input"
                  />
                </div>
                <div>
                  <Label htmlFor="quiet-tz">{t('settings.notificationPrefs.timezone')}</Label>
                  <Select
                    // Radix Select forbids empty-string values — use
                    // `undefined` to represent the "unset" state so the
                    // placeholder renders instead of crashing with
                    // "Select.Item value cannot be empty".
                    value={effectiveRows['vehicle.document.expiring']?.timezone ?? undefined}
                    onValueChange={value => {
                      const next = value.length === 0 ? null : value;
                      for (const entry of NOTIFICATION_CATALOG) {
                        mutate(entry.key, r => ({ ...r, timezone: next }));
                      }
                    }}
                  >
                    <SelectTrigger id="quiet-tz" data-testid="quiet-tz-select">
                      <SelectValue
                        placeholder={t('settings.notificationPrefs.timezonePlaceholder')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-3">
            <div className="text-xs text-muted-foreground" data-testid="dirty-count">
              {dirtyKeys.size > 0
                ? t('settings.notificationPrefs.unsavedChanges', { count: dirtyKeys.size })
                : t('settings.notificationPrefs.allSaved')}
            </div>
            <Button
              onClick={handleSaveAll}
              disabled={dirtyKeys.size === 0 || saveMutation.isPending}
              data-testid="save-all-button"
            >
              {saveMutation.isPending
                ? t('settings.notificationPrefs.saving')
                : t('settings.notificationPrefs.saveAll')}
            </Button>
          </div>

          {isFranchiseAdmin ? (
            <Card className="border-amber-500/40">
              <CardHeader>
                <CardTitle>{t('settings.notificationPrefs.adminResetTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('settings.notificationPrefs.adminResetHint')}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={adminResetUserId}
                    onChange={e => setAdminResetUserId(e.target.value)}
                    placeholder={t('settings.notificationPrefs.adminResetPlaceholder')}
                    data-testid="admin-reset-user-input"
                  />
                  <Button
                    variant="destructive"
                    onClick={handleResetAdmin}
                    disabled={adminResetUserId.trim().length === 0 || resetMutation.isPending}
                    data-testid="admin-reset-button"
                  >
                    {resetMutation.isPending
                      ? t('settings.notificationPrefs.adminResetting')
                      : t('settings.notificationPrefs.adminResetAction')}
                  </Button>
                </div>
                {resetMutation.isSuccess ? (
                  <div
                    className="text-xs text-emerald-600 dark:text-emerald-400"
                    data-testid="admin-reset-success"
                  >
                    {t('settings.notificationPrefs.adminResetSuccess', {
                      count: resetMutation.data.deleted,
                    })}
                  </div>
                ) : null}
                {resetMutation.isError ? (
                  <div
                    className="text-xs text-red-500 dark:text-red-400"
                    data-testid="admin-reset-error"
                  >
                    {t('settings.notificationPrefs.adminResetError')}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
}
